import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Dropdown from '../components/inputFields/DropDown';
import Button from '../components/buttons/Button';
import DateRangePicker from '../components/inputFields/DateRangePicker';
import { statesOfIndia } from '../constants/states';
import {
    downloadB2CClientData,
    downloadB2BClientData,
    downloadProductVisitorData,
    downloadLeadData,
    downloadCategorySalesReport
} from '../services/downloadSheets';

const DownloadSheets = () => {
    const [activeTab, setActiveTab] = useState('b2c');
    const [loading, setLoading] = useState(false);

    // B2C Client Data filters
    const [b2cFilters, setB2cFilters] = useState({
        period: 'yearly',
        channel: 'all',
        start_date: null,
        end_date: null,
        min_amount: '',
        max_amount: '',
        min_orders: '',
        max_orders: '',
        search: '',
        state: ''
    });

    // B2B Client Data filters
    const [b2bFilters, setB2bFilters] = useState({
        period: 'yearly',
        channel: 'all',
        start_date: null,
        end_date: null,
        min_amount: '',
        max_amount: '',
        min_orders: '',
        max_orders: '',
        search: '',
        state: ''
    });

    // Product Visitor Data filters
    const [visitorFilters, setVisitorFilters] = useState({
        start_date: null,
        end_date: null,
        state: ''
    });

    // Lead Data filters
    const [leadFilters, setLeadFilters] = useState({
        start_date: null,
        end_date: null,
        state: ''
    });

    // Category Sales Report filters
    const [categorySalesFilters, setCategorySalesFilters] = useState({
        period: 'yearly',
        start_date: null,
        end_date: null,
        order_type: 'all',
        channel: 'all'
    });

    const periodOptions = [
        { value: 'today', label: 'Today' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'quarterly', label: 'Quarterly' },
        { value: 'yearly', label: 'Yearly' }
    ];

    const channelOptions = [
        { value: 'all', label: 'All' },
        { value: 'offline', label: 'Offline' },
        { value: 'B2C', label: 'B2C' },
        { value: 'website', label: 'Website' },
        { value: 'app', label: 'App' }
    ];

    const orderTypeOptions = [
        { value: 'all', label: 'All' },
        { value: 'B2C', label: 'B2C' },
        { value: 'B2B', label: 'B2B' },
        { value: 'THIRD_PARTY', label: 'Third Party' }
    ];

    const categoryChannelOptions = [
        { value: 'all', label: 'All' },
        { value: 'offline', label: 'Offline' },
        { value: 'website', label: 'Website' },
        { value: 'app', label: 'App' },
        { value: 'telesales', label: 'Telesales' }
    ];

    const stateOptions = [
        { value: '', label: 'All States' },
        ...statesOfIndia.map(state => ({ value: state, label: state }))
    ];

    const handleB2cFilterChange = (field, value) => {
        setB2cFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleB2bFilterChange = (field, value) => {
        setB2bFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleVisitorFilterChange = (field, value) => {
        setVisitorFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleLeadFilterChange = (field, value) => {
        setLeadFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCategorySalesFilterChange = (field, value) => {
        setCategorySalesFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleB2cDownload = async () => {
        setLoading(true);
        try {
            const params = { ...b2cFilters };

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            // Handle date range
            if (params.start_date && params.end_date) {
                params.start_date = params.start_date;
                params.end_date = params.end_date;
            } else {
                delete params.start_date;
                delete params.end_date;
            }

            await downloadB2CClientData(params);
            toast.success('B2C Client Data downloaded successfully!');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download B2C Client Data');
        } finally {
            setLoading(false);
        }
    };

    const handleB2bDownload = async () => {
        setLoading(true);
        try {
            const params = { ...b2bFilters };

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            // Handle date range
            if (params.start_date && params.end_date) {
                params.start_date = params.start_date;
                params.end_date = params.end_date;
            } else {
                delete params.start_date;
                delete params.end_date;
            }

            await downloadB2BClientData(params);
            toast.success('B2B Client Data downloaded successfully!');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download B2B Client Data');
        } finally {
            setLoading(false);
        }
    };

    const handleVisitorDownload = async () => {
        if (!visitorFilters.start_date || !visitorFilters.end_date) {
            toast.error('Please select start and end dates');
            return;
        }

        setLoading(true);
        try {
            const params = { ...visitorFilters };

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            await downloadProductVisitorData(params);
            toast.success('Product Visitor Data downloaded successfully!');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download Product Visitor Data');
        } finally {
            setLoading(false);
        }
    };

    const handleLeadDownload = async () => {
        if (!leadFilters.start_date || !leadFilters.end_date) {
            toast.error('Please select start and end dates');
            return;
        }

        setLoading(true);
        try {
            const params = { ...leadFilters };

            // Remove empty values
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            await downloadLeadData(params);
            toast.success('Lead Data downloaded successfully!');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download Lead Data');
        } finally {
            setLoading(false);
        }
    };

    const handleCategorySalesDownload = async () => {
        setLoading(true);
        try {
            const params = { ...categorySalesFilters };

            // Remove empty/null values
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            // Handle date range
            if (params.start_date && params.end_date) {
                params.start_date = params.start_date;
                params.end_date = params.end_date;
            } else {
                delete params.start_date;
                delete params.end_date;
            }

            await downloadCategorySalesReport(params);
            toast.success('Category Sales Report downloaded successfully!');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download Category Sales Report');
        } finally {
            setLoading(false);
        }
    };

    const renderB2cFilters = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Dropdown
                    label="Period"
                    options={periodOptions}
                    value={b2cFilters.period}
                    onChange={(value) => handleB2cFilterChange('period', value)}
                    placeholder="Select period"
                />

                <Dropdown
                    label="Channel"
                    options={channelOptions}
                    value={b2cFilters.channel}
                    onChange={(value) => handleB2cFilterChange('channel', value)}
                    placeholder="Select channel"
                />

                <Dropdown
                    label="State"
                    options={stateOptions}
                    value={b2cFilters.state}
                    onChange={(value) => handleB2cFilterChange('state', value)}
                    placeholder="Select state"
                />
            </div>

            <DateRangePicker
                label="Custom Date Range (overrides period)"
                value={{ start: b2cFilters.start_date, end: b2cFilters.end_date }}
                onChange={(dateRange) => {
                    handleB2cFilterChange('start_date', dateRange.start);
                    handleB2cFilterChange('end_date', dateRange.end);
                }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Amount
                    </label>
                    <input
                        type="number"
                        value={b2cFilters.min_amount}
                        onChange={(e) => handleB2cFilterChange('min_amount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Minimum amount"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Amount
                    </label>
                    <input
                        type="number"
                        value={b2cFilters.max_amount}
                        onChange={(e) => handleB2cFilterChange('max_amount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Maximum amount"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Orders
                    </label>
                    <input
                        type="number"
                        value={b2cFilters.min_orders}
                        onChange={(e) => handleB2cFilterChange('min_orders', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Minimum orders"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Orders
                    </label>
                    <input
                        type="number"
                        value={b2cFilters.max_orders}
                        onChange={(e) => handleB2cFilterChange('max_orders', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Maximum orders"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                </label>
                <input
                    type="text"
                    value={b2cFilters.search}
                    onChange={(e) => handleB2cFilterChange('search', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by client name, phone, or shop name"
                />
            </div>

            <Button
                onClick={handleB2cDownload}
                disabled={loading}
                className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
                {loading ? 'Downloading...' : 'Download B2C Client Data'}
            </Button>
        </div>
    );

    const renderB2bFilters = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Dropdown
                    label="Period"
                    options={periodOptions}
                    value={b2bFilters.period}
                    onChange={(value) => handleB2bFilterChange('period', value)}
                    placeholder="Select period"
                />

                <Dropdown
                    label="Channel"
                    options={channelOptions}
                    value={b2bFilters.channel}
                    onChange={(value) => handleB2bFilterChange('channel', value)}
                    placeholder="Select channel"
                />

                <Dropdown
                    label="State"
                    options={stateOptions}
                    value={b2bFilters.state}
                    onChange={(value) => handleB2bFilterChange('state', value)}
                    placeholder="Select state"
                />
            </div>

            <DateRangePicker
                label="Custom Date Range (overrides period)"
                value={{ start: b2bFilters.start_date, end: b2bFilters.end_date }}
                onChange={(dateRange) => {
                    handleB2bFilterChange('start_date', dateRange.start);
                    handleB2bFilterChange('end_date', dateRange.end);
                }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Amount
                    </label>
                    <input
                        type="number"
                        value={b2bFilters.min_amount}
                        onChange={(e) => handleB2bFilterChange('min_amount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Minimum amount"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Amount
                    </label>
                    <input
                        type="number"
                        value={b2bFilters.max_amount}
                        onChange={(e) => handleB2bFilterChange('max_amount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Maximum amount"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Orders
                    </label>
                    <input
                        type="number"
                        value={b2bFilters.min_orders}
                        onChange={(e) => handleB2bFilterChange('min_orders', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Minimum orders"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Orders
                    </label>
                    <input
                        type="number"
                        value={b2bFilters.max_orders}
                        onChange={(e) => handleB2bFilterChange('max_orders', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Maximum orders"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search
                </label>
                <input
                    type="text"
                    value={b2bFilters.search}
                    onChange={(e) => handleB2bFilterChange('search', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by client name, phone, or shop name"
                />
            </div>

            <Button
                onClick={handleB2bDownload}
                disabled={loading}
                className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
                {loading ? 'Downloading...' : 'Download B2B Client Data'}
            </Button>
        </div>
    );

    const renderVisitorFilters = () => (
        <div className="space-y-6">
            <DateRangePicker
                label="Date Range"
                value={{ start: visitorFilters.start_date, end: visitorFilters.end_date }}
                onChange={(dateRange) => {
                    handleVisitorFilterChange('start_date', dateRange.start);
                    handleVisitorFilterChange('end_date', dateRange.end);
                }}
                required={true}
            />

            <Dropdown
                label="State"
                options={stateOptions}
                value={visitorFilters.state}
                onChange={(value) => handleVisitorFilterChange('state', value)}
                placeholder="Select state"
            />

            <Button
                onClick={handleVisitorDownload}
                disabled={loading || !visitorFilters.start_date || !visitorFilters.end_date}
                className="bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
                {loading ? 'Downloading...' : 'Download Product Visitor Data'}
            </Button>
        </div>
    );

    const renderLeadFilters = () => (
        <div className="space-y-6">
            <DateRangePicker
                label="Date Range"
                value={{ start: leadFilters.start_date, end: leadFilters.end_date }}
                onChange={(dateRange) => {
                    handleLeadFilterChange('start_date', dateRange.start);
                    handleLeadFilterChange('end_date', dateRange.end);
                }}
                required={true}
            />

            <Dropdown
                label="State"
                options={stateOptions}
                value={leadFilters.state}
                onChange={(value) => handleLeadFilterChange('state', value)}
                placeholder="Select state"
            />

            <Button
                onClick={handleLeadDownload}
                disabled={loading || !leadFilters.start_date || !leadFilters.end_date}
                className="bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
            >
                {loading ? 'Downloading...' : 'Download Lead Data'}
            </Button>
        </div>
    );

    const renderCategorySalesFilters = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Dropdown
                    label="Period"
                    options={[
                        { value: 'all', label: 'All' },
                        ...periodOptions
                    ]}
                    value={categorySalesFilters.period}
                    onChange={(value) => handleCategorySalesFilterChange('period', value)}
                    placeholder="Select period"
                />

                <Dropdown
                    label="Order Type"
                    options={orderTypeOptions}
                    value={categorySalesFilters.order_type}
                    onChange={(value) => handleCategorySalesFilterChange('order_type', value)}
                    placeholder="Select order type"
                />

                <Dropdown
                    label="Channel"
                    options={categoryChannelOptions}
                    value={categorySalesFilters.channel}
                    onChange={(value) => handleCategorySalesFilterChange('channel', value)}
                    placeholder="Select channel"
                />
            </div>

            <DateRangePicker
                label="Custom Date Range (overrides period when both start and end dates are selected)"
                value={{ start: categorySalesFilters.start_date, end: categorySalesFilters.end_date }}
                onChange={(dateRange) => {
                    handleCategorySalesFilterChange('start_date', dateRange.start);
                    handleCategorySalesFilterChange('end_date', dateRange.end);
                }}
            />

            <Button
                onClick={handleCategorySalesDownload}
                disabled={loading}
                className="bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
                {loading ? 'Downloading...' : 'Download Category Sales Report'}
            </Button>
        </div>
    );


    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Download Sheets</h1>
                <p className="text-gray-600">Download various data reports in Excel format</p>
            </div>

            {/* Tabs */}
            <div className="mb-6">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('b2c')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'b2c'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            B2C Client Data
                        </button>
                        <button
                            onClick={() => setActiveTab('b2b')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'b2b'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            B2B Client Data
                        </button>
                        <button
                            onClick={() => setActiveTab('visitor')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'visitor'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Product Visitor Data
                        </button>
                        <button
                            onClick={() => setActiveTab('lead')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'lead'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Lead Data
                        </button>
                        <button
                            onClick={() => setActiveTab('category_sales')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'category_sales'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Category Sales Report
                        </button>
                    </nav>
                </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow p-6">
                {activeTab === 'b2c' && renderB2cFilters()}
                {activeTab === 'b2b' && renderB2bFilters()}
                {activeTab === 'visitor' && renderVisitorFilters()}
                {activeTab === 'lead' && renderLeadFilters()}
                {activeTab === 'category_sales' && renderCategorySalesFilters()}
            </div>
        </div>
    );
};

export default DownloadSheets;
